// Internal helpers of the GPX module (no Swift counterpart; not re-exported by index.ts).

/**
 * Removes the properties whose value is `undefined`, so a Swift `nil` becomes an absent optional property
 * (the port's convention) rather than a key holding `undefined`.
 */
export function omitUndefined<T extends object>(value: T): T {
  for (const key of Object.keys(value) as Array<keyof T>) {
    if (value[key] === undefined) delete value[key];
  }
  return value;
}

/** Sets an optional property, or removes it when `value` is `undefined` (Swift `x.property = optional`). */
export function assignOptional<T extends object, K extends keyof T>(target: T, key: K, value: T[K] | undefined): void {
  if (value === undefined) delete target[key];
  else target[key] = value;
}

/**
 * Swift `CharacterSet.whitespacesAndNewlines`: Unicode general category Z*, U+0009…U+000D and U+0085.
 * (JavaScript's `trim` also strips U+FEFF and keeps U+0085, so it is not used.)
 */
export function isWhitespaceOrNewline(code: number): boolean {
  return isWhitespace(code) || (code >= 0x0a && code <= 0x0d) || code === 0x85 || code === 0x2028 || code === 0x2029;
}

/** Swift `CharacterSet.whitespaces`: Unicode general category Zs and U+0009. */
export function isWhitespace(code: number): boolean {
  return (
    code === 0x09 ||
    code === 0x20 ||
    code === 0xa0 ||
    code === 0x1680 ||
    (code >= 0x2000 && code <= 0x200a) ||
    code === 0x202f ||
    code === 0x205f ||
    code === 0x3000
  );
}

/** Swift `trimmingCharacters(in:)` for a set given as a code-unit predicate (all members are in the BMP). */
export function trimCharacters(s: string, isMember: (code: number) => boolean): string {
  let start = 0;
  let end = s.length;
  while (start < end && isMember(s.charCodeAt(start))) start += 1;
  while (end > start && isMember(s.charCodeAt(end - 1))) end -= 1;
  return start === 0 && end === s.length ? s : s.slice(start, end);
}

/** Swift `trimmingCharacters(in: .whitespacesAndNewlines)`. */
export function trimWhitespaceAndNewlines(s: string): string {
  return trimCharacters(s, isWhitespaceOrNewline);
}
